import React from 'react';
import { FadeIn } from './FadeIn';
import { motion } from 'framer-motion';
import { DollarSign, Megaphone, ClipboardCheck, TrendingUp, Ruler, Sparkles, Check } from 'lucide-react';
const modules = [{
  num: 1,
  icon: <DollarSign size={20} data-id="element-100" />,
  title: 'Aumento de Honorarios',
  hook: 'Cobrá lo que realmente vale tu trabajo, sin culpa ni miedo.',
  bullets: ['Cómo calcular tu hora profesional real', 'Guion para comunicar aumentos sin perder pacientes', 'Estrategias de pricing por tipo de consulta']
}, {
  num: 2,
  icon: <Megaphone size={20} data-id="element-101" />,
  title: 'Marketing y Captación de Pacientes',
  hook: 'Llená tu agenda con pacientes ideales de forma constante.',
  bullets: ['Plantillas de contenido para redes que convierten', 'Sistema de referidos y boca en boca', 'Cómo posicionarte como referente local']
}, {
  num: 3,
  icon: <ClipboardCheck size={20} data-id="element-102" />,
  title: 'Gestión',
  hook: 'Ordená tu consultorio y dejá de perder tiempo en lo administrativo.',
  bullets: ['Sistema de agenda y reservas eficiente', 'Plantillas de facturación e historia clínica', 'Procesos para no olvidar ningún seguimiento']
}, {
  num: 4,
  icon: <TrendingUp size={20} data-id="element-103" />,
  title: 'Estrategia de Negocios',
  hook: 'Pensá tu consultorio como una empresa rentable y escalable.',
  bullets: ['Modelo de ingresos: 1:1, grupos y productos digitales', 'Indicadores clave para medir tu crecimiento', 'Plan de expansión paso a paso']
}, {
  num: 5,
  icon: <Ruler size={20} data-id="element-104" />,
  title: 'Evaluación Antropométrica',
  hook: 'Mediciones precisas que respaldan tus decisiones clínicas.',
  bullets: ['Protocolo completo de toma de medidas', 'Interpretación de pliegues y perímetros', 'Plantillas para informes profesionales']
}];
export function KitModules() {
  return <section className="relative py-12 md:py-16 overflow-hidden" data-id="element-105">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none opacity-60" style={{
      background: 'radial-gradient(ellipse at 50% 0%, rgba(74,222,128,0.10) 0%, transparent 60%)'
    }} data-id="element-106" />

      <div className="relative max-w-5xl mx-auto px-4" data-id="element-107">
        {/* Section header */}
        <FadeIn className="text-center mb-10" data-id="element-108">
          <span className="inline-flex items-center gap-1.5 py-1 px-3 rounded-full bg-mint/10 text-mint font-semibold text-xs mb-3 tracking-widest uppercase border border-mint/20" data-id="element-109">
            <Sparkles size={12} data-id="element-110" />
            Kit Profesional · 5 Módulos
          </span>
          <h2 className="font-['Open_Sans',_sans-serif] font-[900] text-3xl md:text-4xl text-dark leading-tight" data-id="element-111">
            Lo que incluye el <span className="text-mint" data-id="element-112">Kit Profesional</span>
          </h2>
          <p className="text-dark/60 text-sm md:text-base mt-3 max-w-2xl mx-auto" data-id="element-113">
            5 módulos diseñados para que dejes de ser "solo nutricionista" y te
            conviertas en un profesional rentable, visible y bien organizado.
          </p>
        </FadeIn>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-2 gap-4 md:gap-5" data-id="element-114">
          {modules.map((mod, idx) => <FadeIn key={idx} delay={idx * 0.08} data-id="element-115">
              <motion.div className={`group relative bg-surface rounded-2xl border border-mint/15 h-full flex flex-col shadow-lg shadow-black/40 hover:border-mint/50 transition-all overflow-hidden ${idx === modules.length - 1 && modules.length % 2 !== 0 ? 'md:col-span-2 md:max-w-[calc(50%-0.625rem)] md:mx-auto md:w-full' : ''}`} whileHover={{
            y: -4,
            boxShadow: '0 20px 40px -15px rgba(74,222,128,0.25)'
          }} transition={{
            duration: 0.3,
            ease: 'easeOut'
          }} data-id="element-116">
                {/* Top gradient ribbon */}
                <div className="h-1 w-full bg-gradient-to-r from-mint via-secondary to-mint/40" data-id="element-117" />

                {/* Decorative number watermark */}
                <span aria-hidden className="absolute -top-4 -right-2 text-[7rem] font-[900] font-['Open_Sans',_sans-serif] text-mint/[0.06] leading-none select-none pointer-events-none" data-id="element-118">
                  {String(mod.num).padStart(2, '0')}
                </span>

                <div className="relative p-5 md:p-6 flex flex-col h-full" data-id="element-119">
                  {/* Header row */}
                  <div className="flex items-center gap-3 mb-4" data-id="element-120">
                    <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-mint/20 to-mint/5 border border-mint/30 flex items-center justify-center text-mint shadow-inner" data-id="element-121">
                      {mod.icon}
                    </div>
                    <div data-id="element-122">
                      <p className="text-mint/80 text-[10px] font-bold tracking-[0.2em] uppercase" data-id="element-123">
                        Módulo {String(mod.num).padStart(2, '0')}
                      </p>
                      <h3 className="font-['Open_Sans',_sans-serif] font-[800] text-lg md:text-xl text-dark leading-snug group-hover:text-mint transition-colors" data-id="element-124">
                        {mod.title}
                      </h3>
                    </div>
                  </div>

                  {/* Marketing hook */}
                  <p className="text-mint italic text-sm md:text-base font-semibold mb-4 border-l-2 border-mint/40 pl-3" data-id="element-125">
                    {mod.hook}
                  </p>

                  {/* Bullets */}
                  <ul className="space-y-2 mb-2" data-id="element-126">
                    {mod.bullets.map((b, i) => <li key={i} className="flex items-start gap-2.5 text-dark/80 text-sm leading-relaxed" data-id="element-127">
                        <span className="shrink-0 mt-0.5 w-4 h-4 rounded-full bg-mint/15 border border-mint/30 flex items-center justify-center" data-id="element-128">
                          <Check size={10} strokeWidth={3} className="text-mint" data-id="element-129" />
                        </span>
                        <span data-id="element-130">{b}</span>
                      </li>)}
                  </ul>
                </div>
              </motion.div>
            </FadeIn>)}
        </div>

        {/* Bottom badge */}
        <FadeIn delay={0.6} className="text-center mt-8" data-id="element-131">
          <div className="inline-flex items-center gap-3 bg-surface border border-mint/20 rounded-full px-6 py-3 shadow-lg shadow-black/40" data-id="element-132">
            <span className="text-lg" data-id="element-133">🚀</span>
            <p className="text-dark/80 text-sm font-medium" data-id="element-134">
              Solo incluido en el{' '}
              <strong className="text-mint" data-id="element-135">Megapack + Kit Profesional</strong>
            </p>
          </div>
        </FadeIn>
      </div>
    </section>;
}